import moment from "moment-timezone";
import Vue from "vue";

// Queries
import FETCH_ORGANIZATIONS from "../../graphql/query/FetchOrganizations.gql";
import GET_HOMOLOGATIONS_BY_ORGANIZATION_ID from "../../graphql/query/GetHomologationsByOrganizationId.gql";
// Mutations
import CREATE_ORGANIZATION from "../../graphql/mutation/CreateOrganization.gql";
import REMOVE_ORGANIZATION from "../../graphql/mutation/RemoveOrganization.gql";
import UPDATE_ORGANIZATION from "../../graphql/mutation/UpdateOrganization.gql";
import UNLINK_PROGRAM from "../../graphql/mutation/UnlinkProgram.gql";

export const namespaced = true;

const initState = () => {
  return {
    organizations: [],
    isOrganizationsLoading: false,
    membershipBalance: {
      membership: {
        level: "unknown",
        expireAt: null,
        total: 0,
      },
      balance: {
        total: 0,
        available: 0,
        used: 0,
        reserved: 0,
      },
    },
  };
};

export const state = initState();

export const mutations = {
  SET_ORGANIZATIONS(state, organizations) {
    state.organizations = organizations;
  },
  SET_LOADING(state, status) {
    state.isOrganizationsLoading = status;
  },
  REMOVE_ORGANIZATION(state, removedOrganizationId) {
    state.organizations = state.organizations.filter(
      ({ _id }) => _id !== removedOrganizationId
    );
  },
  UPDATE_ORGANIZATION(state, updatedOrganization) {
    const organizationIndex = state.organizations.findIndex(
      (org) => org._id === updatedOrganization._id
    );
    if (organizationIndex === -1) return;
    Vue.set(state.organizations, organizationIndex, updatedOrganization);
  },
  RESET_STATE(state) {
    Object.assign(state, initState());
  },
  SET_MEMBERSHIP_BALANCE(state, balance) {
    state.membershipBalance = balance;
  },
};

export const actions = {
  async createOrganization({ commit }, { apolloClient, ...data }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { CreateOrganization: organization },
      } = await apolloClient.mutate({
        mutation: CREATE_ORGANIZATION,
        fetchPolicy: "no-cache",
        variables: {
          data,
        },
      });

      if (!organization) throw new Error("No se pudo crear la organización");

      return organization;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async fetchOrganizations(
    { commit },
    { filter, limit, skip, apolloClient, includeMembershipData, restClient }
  ) {
    try {
      commit("SET_LOADING", true);
      if (restClient) {
        const path = `${
          process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"
        }/organizations`;
        const organizations = await restClient.get(path);
        commit("SET_ORGANIZATIONS", organizations.data.organizations);
        return organizations.data.organizations;
      }
      const {
        data: { FetchOrganizations: organizations },
      } = await apolloClient.query({
        query: FETCH_ORGANIZATIONS,
        fetchPolicy: "no-cache",
        variables: {
          data: {
            filter,
            limit,
            skip,
            includeMembershipData,
          },
        },
      });
      commit("SET_ORGANIZATIONS", organizations);
      return organizations;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async removeOrganization({ commit }, { _id, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { RemoveOrganization: removedOrganization },
      } = await apolloClient.mutate({
        mutation: REMOVE_ORGANIZATION,
        fetchPolicy: "no-cache",
        variables: {
          data: {
            _id,
          },
        },
      });
      if (!removedOrganization._id)
        throw new Error("Hubo un error eliminando la organización");
      commit("REMOVE_ORGANIZATION", removedOrganization._id);
      return removedOrganization;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async updateOrganization({ commit }, { _id, data, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { UpdateOrganization: updatedOrganization },
      } = await apolloClient.mutate({
        mutation: UPDATE_ORGANIZATION,
        fetchPolicy: "no-cache",
        variables: {
          _id,
          data,
        },
      });
      if (!updatedOrganization._id)
        throw new Error("Hubo un error eliminando la organización");
      commit("UPDATE_ORGANIZATION", updatedOrganization);
      return updatedOrganization;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async unlinkProgram({ commit }, { organizationId, programId, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { UnlinkProgram: updatedOrganization },
      } = await apolloClient.mutate({
        mutation: UNLINK_PROGRAM,
        fetchPolicy: "no-cache",
        variables: { organizationId, programId },
      });
      return updatedOrganization;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async getHomologationsByOrganizationId(
    { commit },
    { apolloClient, organizationId }
  ) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { GetHomologationsByOrganizationId: homologations },
      } = await apolloClient.query({
        query: GET_HOMOLOGATIONS_BY_ORGANIZATION_ID,
        fetchPolicy: "no-cache",
        variables: {
          organizationId,
        },
      });
      return homologations;
    } catch (error) {
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async fetchMembershipBalance({ commit }, { organizationId, restClient }) {
    try {
      commit("SET_LOADING", true);
      const response = await restClient.get(
        `/api/v1/membership/balance?organizationId=${organizationId}`
      );
      const data = response.data;
      commit("SET_MEMBERSHIP_BALANCE", data);
      return data;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Fetches credit balance for an organization using REST API
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {Object} options.restClient - REST client instance
   * @param {boolean} options.withSubAccounts - Whether to include sub-accounts
   * @returns {Promise<Object>} Normalized credit balance object
   */
  async fetchOrganizationCreditBalance(
    { commit },
    { organizationId, restClient, withSubAccounts = true }
  ) {
    try {
      commit("SET_LOADING", true);
      const response = await restClient.get(
        `/api/v1/organizations/account-balances?organizationId=${organizationId}&withSubAccounts=${withSubAccounts}`
      );

      return response.data[0].balances;
    } catch (error) {
      console.error("Error fetching organization credit balance:", error);
      return {
        total: 0,
        available: 0,
        reserved: 0,
        used: 0,
        expired: 0,
      };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Fetches credit movements for an organization using REST API
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {Object} options.restClient - REST client instance
   * @param {number} options.page - Page number for pagination
   * @param {number} options.pageSize - Number of items per page
   * @param {string} options.sortBy - Sorting criteria (e.g., "createdAt:desc")
   * @returns {Promise<Object>} Movements data with pagination
   */
  async fetchOrganizationCreditMovements(
    { commit },
    {
      organizationId,
      restClient,
      page = 1,
      pageSize = 10,
      sortBy = "createdAt:desc",
    }
  ) {
    try {
      commit("SET_LOADING", true);

      // Prepare query parameters
      const params = new URLSearchParams({
        organizationId,
        page,
        pageSize,
        sortBy,
      });

      const response = await restClient.get(
        `/api/v1/organizations/movements?${params.toString()}`
      );

      // Map the response to a more usable format
      const movements = (response.data?.movements || []).map((movement) => {
        return {
          id: movement.id,
          createdAt: movement.date || movement.createdAt || new Date().toISOString(),
          amount: parseFloat(movement.amount),
          type: movement.operationType,
          operationType: movement.operationType,
          programId: movement.programId,
          programName: movement.programName,
          traveler: movement.description || "",
          description: movement.description || "",
          account: movement.account?.code || "",
          journal: movement.journal?.documentId || "",
        };
      });

      // Prepare pagination information
      const pagination = {
        page: response.data?.pagination?.page || page,
        pageSize: response.data?.pagination?.pageSize || pageSize,
        total: response.data?.pagination?.total || movements.length,
      };

      return {
        movements,
        pagination,
      };
    } catch (error) {
      console.error("Error fetching organization credit movements:", error);
      return {
        movements: [],
        pagination: {
          page,
          pageSize,
          total: 0,
        },
      };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Adds credits to an organization using REST API
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {number} options.amount - Amount of credits to add
   * @param {string} options.comment - Optional comment for the transaction
   * @param {Object} options.restClient - REST client instance
   * @returns {Object} - Response data with transaction details
   */
  async addCreditsToOrganization(
    { commit },
    { organizationId, amount, comment = "", restClient }
  ) {
    try {
      commit("SET_LOADING", true);

      const response = await restClient.post("/api/v1/organizations/add-credits", {
        organizationId,
        amount: parseInt(amount, 10),
        description: comment,
      });

      return response.data;
    } catch (error) {
      console.error("Error adding credits to organization:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Subtracts credits from an organization using REST API
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {number} options.amount - Amount of credits to subtract
   * @param {string} options.reason - Reason for the subtraction
   * @param {Object} options.restClient - REST client instance
   * @returns {Object} - Response data with transaction details
   */
  async subtractCreditsFromOrganization(
    { commit },
    { organizationId, amount, reason = "", restClient }
  ) {
    try {
      commit("SET_LOADING", true);

      const response = await restClient.post(
        "/api/v1/organizations/subtract-credits",
        {
          organizationId,
          amount: Math.abs(parseInt(amount, 10)),
          reason,
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error subtracting credits from organization:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * Adds a program to an organization's programs array
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {string} options.organizationId - Organization ID
   * @param {string} options.programId - Program ID to add
   * @param {Object} options.apolloClient - Apollo client instance
   * @returns {Object} - Updated organization data
   */
  async addProgramToOrganization(
    { commit, state },
    { organizationId, programId, apolloClient }
  ) {
    try {
      commit("SET_LOADING", true);

      // Find the organization to update
      const organization = state.organizations.find(
        (org) => org._id === organizationId
      );
      if (!organization) {
        throw new Error("Organization not found");
      }

      // Create a new programs array with only program IDs
      const existingPrograms = organization.programs || [];
      const programs = existingPrograms.map((program) =>
        typeof program === "object" && program._id ? program._id : program
      );

      // Add the new program ID if not already included
      if (!programs.includes(programId)) {
        programs.push(programId);
      }

      // Update the organization
      const {
        data: { UpdateOrganization: updatedOrganization },
      } = await apolloClient.mutate({
        mutation: UPDATE_ORGANIZATION,
        variables: {
          _id: organizationId,
          data: {
            programs,
          },
        },
      });

      // Update the local state
      commit("UPDATE_ORGANIZATION", updatedOrganization);

      return updatedOrganization;
    } catch (error) {
      console.error("Error adding program to organization:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export const getters = {
  organizations: (state, getters, rootState, rootGetters) => {
    const dateFormat = "DD-MM-YYYY";
    const timezone = rootGetters["users/getTimezone"];
    return state.organizations.map((organization) => {
      return {
        ...organization,
        createdAt: organization.createdAt
          ? moment.tz(+organization.createdAt, timezone).format(dateFormat)
          : null,
        updatedAt: organization.updatedAt
          ? moment.tz(+organization.updatedAt, timezone).format(dateFormat)
          : null,
        programs: organization.programs ?? [],
        totalPrograms: organization.programs?.length ?? 0,
      };
    });
  },
  isOrganizationsLoading: (state) => state.isLoading,
  membershipBalance: (state) => state.membershipBalance,
};
